import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { formatDateTime } from '@/lib/format';
import { notificationBody, notificationMeta, notificationTitle } from '@/lib/notificationContent';
import { useNotifications } from '@/context/NotificationsContext';
import type { NotificationView } from '@/types';

interface NotificationItemProps {
  notification: NotificationView;
}

export default function NotificationItem({ notification }: NotificationItemProps) {
  const { t, i18n } = useTranslation();
  const { markProcessed } = useNotifications();
  const meta = notificationMeta(notification.kind);

  const actionLabel = notification.kind === 'new_review'
    ? notification.to?.includes('/drivers/')
      ? t('notifications.openDriverReviews')
      : t('notifications.openReviews')
    : t('notifications.openOrder');

  const showNoDestination =
    !notification.to && !notification.targetMissing && notification.kind === 'package_subscription';

  return (
    <li
      className={cn(
        'flex gap-3.5 px-4 py-4 transition-colors hover:bg-background-100/70 md:px-5',
        notification.processed && 'bg-background-100/50',
      )}
    >
      <span
        className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-md',
          meta.tone,
        )}
      >
        <i className={cn(meta.icon, 'text-lg leading-none')} aria-hidden="true" />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <p className="text-sm font-semibold text-foreground-950">
            {notificationTitle(notification, t)}
          </p>
          {notification.processed ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-2 py-0.5 text-[11px] font-semibold text-accent-800">
              <i className="ri-checkbox-circle-fill text-sm leading-none" aria-hidden="true" />
              {t('notifications.processed')}
            </span>
          ) : null}
        </div>
        <p className="mt-1 break-words text-sm leading-relaxed text-foreground-600">
          {notificationBody(notification, t)}
        </p>

        {notification.targetMissing ? (
          <p className="mt-2 flex items-start gap-1.5 text-xs leading-relaxed text-rose-700">
            <i className="ri-error-warning-line mt-0.5 text-base" aria-hidden="true" />
            {t('notifications.targetMissing')}
          </p>
        ) : showNoDestination ? (
          <p className="mt-2 flex items-start gap-1.5 text-xs leading-relaxed text-foreground-500">
            <i className="ri-information-line mt-0.5 text-base text-foreground-400" aria-hidden="true" />
            {t('notifications.noDestination')}
          </p>
        ) : null}

        <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-background-100 pt-2.5 text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-time-line text-base text-foreground-400" aria-hidden="true" />
            {formatDateTime(notification.createdAt, i18n.language)}
          </span>

          {notification.orderId ? (
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-file-list-3-line text-base text-foreground-400" aria-hidden="true" />
              {t('notifications.orderRef')}:
              <span className="lb-ltr-field font-medium text-foreground-700">{notification.orderId}</span>
            </span>
          ) : null}

          {notification.to ? (
            <Link
              to={notification.to}
              className="inline-flex items-center gap-1 font-medium text-primary-700 transition-colors hover:text-primary-800"
            >
              <i className="ri-arrow-left-line text-base rtl:rotate-180" aria-hidden="true" />
              {actionLabel}
            </Link>
          ) : null}

          <button
            type="button"
            onClick={() => markProcessed(notification.id, !notification.processed)}
            aria-pressed={notification.processed}
            className={cn(
              'inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 font-medium transition-colors',
              notification.processed
                ? 'border-accent-200 bg-accent-50 text-accent-800 hover:bg-accent-100'
                : 'border-background-300 text-foreground-600 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700',
            )}
          >
            <i
              className={cn(
                notification.processed ? 'ri-arrow-go-back-line' : 'ri-check-double-line',
                'text-base leading-none',
              )}
              aria-hidden="true"
            />
            {notification.processed
              ? t('notifications.unmarkProcessed')
              : t('notifications.markProcessed')}
          </button>
        </div>
      </div>
    </li>
  );
}